import { distinct } from "../common/utils/arrayUtilities";

export const estimateWeight = (isPaediatric, age) => {
    if (age === null || !isPaediatric) {
        return null;
    }
    return (age * 3) + 7;
}


export const getTreatmentRoutes = (isPaediatric, dosing) => {
    if (!dosing?.adult && !dosing?.paediatric) {
        return ['PO', 'IM', 'IV', 'SubCut', 'IO', 'Subling', 'NAS', 'Neb', 'INH', 'Top', 'Discard'];
    } else if (isPaediatric === undefined) {
        return distinct(Object.keys(dosing.adult).concat(Object.keys(dosing.paediatric)));
    } else if (!isPaediatric) {
        return Object.keys(dosing.adult)
    } else if (dosing.paediatric) {
        return Object.keys(dosing.paediatric);
    }
    return [];
}

export const calculateDose = (isPaediatric, age, setWeight, route, med) => {
    /* Dosage amounts can be:
        1) an array of objects corresponding to a weight/age range (each element contains standard_dose, max, etc.)
        2) a single or array of two (i.e. a range) standard amount(s)
        3) an amount per kg (dose_rate) of either
            a) a single set amount
            b) an array of two values representing a range (min and max)

     *** Potential Variables ***
        When dealing with doses that vary by a particular weight or age band (mainly/only for paediatric cases)
        - min_weight/min_age -> representing the minimum weight/age for that band (may be null if this is the first band)
        - max_weight/max_age -> representing the maxiumum weight/age for that band (may be null if this is the last band)

        Informational Variables
        - min_dose -> minumum amount to give in a single dose
        - max_dose -> maximum amount to give in a single does
        - max -> total maxumum amount that can be administered
        - units -> units of measurement for the dose (e.g. mL, mg)
        - step -> smallest amount allowed to increase or decrease the dose by
        - notes -> free text with any additional information about the medication/dosing

        Dose Amounts - used to pre-populate the dose field (these are mutually exclusive)
        - standard_dose -> either a) a single value or
                                  b) an array of two values representing standard dose as a range. In this case the lower value (first element) will be used to prepopulate the dose field
        - dose_rate -> an amount per kg. This will be multiplied by weight (if available/can be estimated) and used to prepopulate the dose field
                       As in the case of standard_dose, this may be either a single value or a two element array representing a range, in which case the first element will be used in the calculation
    
    *** Examples ***
    {
      "max": 200,
      "units": "microgs",
      "max_dose": 100,
      "standard_dose": 50
    }
------------------------------
    {
      "max": "Repeated Once",
      "units": "mmol",
      "dose_rate": 0.1,
      "step": "0.1"
    }
------------------------------    
    {
      "max": null,
      "units": "microgs",
      "max_dose": 300,
      "min_dose": 0,
      "standard_dose": [150, 300]
    }
------------------------------
    [
      {
        "max": 0.1,
        "units": "mg",
        "max_age": 1,
        "standard_dose": 0.1
        "step": 0.1
      },
      {
        "max": 0.15,
        "units": "mg",
        "max_age": 5,
        "min_age": 1,
        "standard_dose": 0.15,
        "step": 0.01
      },
      {
        "max": 0.3,
        "units": "mg",
        "min_age": 5,
        "standard_dose": 0.3,
        "step": 0.1
      }
    ]
    
    */
    if (!(route && ((!isPaediatric && med?.dosing?.adult) || (isPaediatric && med?.dosing?.paediatric)))) {
        return null;
    }
    let dose = isPaediatric ? med.dosing.paediatric[route] : med.dosing.adult[route];
    let band = ''
    let doseKind = null;
    if (Array.isArray(dose) && dose.length > 0) {
        // are we dealing with an age-based or weight-based drug?
        doseKind = dose.some(d => d.max_weight || d.min_weight) ? 'weight' : 'age';
        // can we find a dose? weight can use age for kids as a proxy
        if (doseKind === 'weight' && !((setWeight || isPaediatric))) {
            return null;
        }
        if (doseKind === 'age' && !age) {
            return null;
        }
        const prop = doseKind === 'age' ? age : (setWeight ?? estimateWeight(isPaediatric, age));
        const max = 'max_' + doseKind;
        const min = 'min_' + doseKind;
        dose = dose.find(d => prop < d[max] || d[max] == null);

        // we are dealing with a banded dosing so compute the info for the band
        band = '';
        if (!dose[min] && dose[max]) {
            band += '< ' + dose.max;
        } else if (dose[min] && dose[max]) {
            band += dose[min] + ' < ' + dose[max];
        } else if (dose[min] && !dose[max]) {
            band += '> ' + dose[min];
        }
        band += (doseKind === 'age' ? 'yo' : 'kg')
    }
    const doseData = {
        doseKind,
        band,
        max: dose?.max !== undefined ? (dose.max ?? 'No Max') + (typeof dose.max === 'number' ? dose.units : '') : '',
        range: (dose?.min_dose || dose?.max_dose) ? ((dose.min_dose ?? '0') + ' - ' + (dose.max_dose ?? 'No Max') + dose.units) : '',
        notes: dose?.notes
    }
        
    let doseAmount = null;
    if (dose?.standard_dose) {
        if (Array.isArray(dose.standard_dose)) {
            doseAmount = dose.standard_dose[0];
            doseData.standardDose = dose.standard_dose[0] + ' - ' + dose.standard_dose[1] + dose.units;
        } else {
            doseAmount = dose.standard_dose;
            doseData.standardDose = dose.standard_dose + dose.units;
        }
    } else if (dose?.dose_rate) {
        let weight = setWeight ?? estimateWeight(isPaediatric, age);
        let info = null;
        if (Array.isArray(dose.dose_rate)) {
            info = '[' + dose.dose_rate[0] + ' - ' + dose.dose_rate[1] + ']';
            if (weight != null) {
                // calculate dose amount (taking the lower end of the range) limiting to 2 decimal places
                doseAmount = Math.round(dose.dose_rate[0] * weight * 100) / 100;
            }
        } else {
            info = dose.dose_rate;
            if (weight != null) {
                // calculate dose amount limiting to 2 decimal places
                doseAmount = Math.round(dose.dose_rate * weight * 100) / 100;
            }
        }
        info += dose.units;
        if (weight != null) {
            info += ' * ' + (setWeight ?? (weight.toFixed(1))) + 'kg';
        } else {
            info += dose.units + '/kg';
        }
        doseData.doseRate = info;
    }
    doseData.units = dose?.units;
    doseData.step = dose?.step;
    doseData.amount = doseAmount;
    return doseData;
}